import ApplicationModel from '../models/applications/api/ApplicationModel';
import AppliedApplicationTemplateModel from '../models/templating/api/AppliedApplicationTemplateModel';
import { TelephonyDeploymentResponse } from '../models/telephonyDeployment/TelephonyDeploymentResponse';
import { FoodOrderingFeatureFlagId, GenAIFoodOrdering, HealthcareSchedulingFeatureFlagId } from '../constants/featureFlagIds';

export const AdminCancellationCodeVariable = '{DenticonCancelCode}';
export const SandboxAdminCancellationCode = '473';

export function hasSandboxAdminCancellationCode(getEffectiveFieldValueByVariable: (variable: string) => string): boolean {
    return getEffectiveFieldValueByVariable(AdminCancellationCodeVariable).trim() === SandboxAdminCancellationCode;
}

export function getGoLiveWarnings(
    app: ApplicationModel,
    appliedTemplates: AppliedApplicationTemplateModel[],
    sharedTemplates: AppliedApplicationTemplateModel[],
    telephonyDeployments: TelephonyDeploymentResponse[]
): string[] {
    const msgs: string[] = [];
    const isAiOrdering = app.featureFlags?.some(ff => ff.id === FoodOrderingFeatureFlagId || ff.id === GenAIFoodOrdering);
    const isDental = app.featureFlags?.some(ff => ff.id === HealthcareSchedulingFeatureFlagId);

    const getFieldValueFromTemplates = (templates: any[], variable: string): string => {
        for (const appliedTemplate of templates) {
            const sections = appliedTemplate.templateForm?.templateFormSections ?? [];
            const field = sections
                .flatMap(s => s.templateFormFields ?? [])
                .find(f => f.associatedVariable === variable);
            const fieldValueObj = (appliedTemplate.templateFormFieldValues ?? []).find(
                fv => fv.templateFormFieldId === field?.id || fv.associatedVariable === variable
            );
            const val = (fieldValueObj?.value ?? field?.defaultValue ?? '').toString().trim();
            if (val !== '' && val !== variable) {
                return val;
            }
        }
        return '';
    };

    const getEffectiveFieldValueByVariable = (variable: string) => {
        const locVal = getFieldValueFromTemplates(appliedTemplates, variable);
        if (locVal) return locVal;
        return getFieldValueFromTemplates(sharedTemplates, variable);
    };

    const sandboxIssues: string[] = [];
    const otherIssues: string[] = [];

    // 1. AI Ordering checks
    if (isAiOrdering) {
        const isTestOrder = appliedTemplates.some(appliedTemplate => {
            const sections = appliedTemplate.templateForm?.templateFormSections ?? [];
            const testOrderField = sections
                .flatMap(s => s.templateFormFields ?? [])
                .find(f => f.associatedVariable === '{Webhook-Is-Test-Order}');
            if (!testOrderField) return false;
            const fieldValue = (appliedTemplate.templateFormFieldValues ?? []).find(
                fv => fv.templateFormFieldId === testOrderField.id
                    || fv.associatedVariable === '{Webhook-Is-Test-Order}'
            );
            const val = (fieldValue?.value ?? '').toString().toLowerCase();
            return val === 'true';
        });
        if (isTestOrder) {
            otherIssues.push('this location is marked as a test app, but it is currently live (please set the Is Test field to False)');
        }

        const restaurantId = getEffectiveFieldValueByVariable('{Webhook-Restaurant-Id}').trim();
        if (restaurantId === '10138') {
            sandboxIssues.push('Admin - Location ID (10138)');
        } else if (restaurantId === '6716858da59fcf87d60ad76b') {
            sandboxIssues.push('Admin - Location ID (6716858da59fcf87d60ad76b)');
        } else if (restaurantId.toLowerCase() === 'af8e3bf4-a16b-4cca-b7a7-bb1be0a16021') {
            sandboxIssues.push('Admin - Location ID (af8e3bf4-a16b-4cca-b7a7-bb1be0a16021)');
        }
    }

    // The cancellation code field is authoritative. Do not skip this warning when
    // an app's loaded feature flags are incomplete or stale.
    if (hasSandboxAdminCancellationCode(getEffectiveFieldValueByVariable)) {
        sandboxIssues.push('Admin Cancellation Code ID (473)');
    }

    // 2. Dental Scheduling checks
    if (isDental) {
        const referralCode = getEffectiveFieldValueByVariable('{denticon-referral-type-code}').trim().toLowerCase();
        if (referralCode === 'roo6' || referralCode === 'r006') {
            sandboxIssues.push('Admin Referral Type Code (ROO6)');
        }

        const defaultOfficeId = getEffectiveFieldValueByVariable('{defaultOfficeId}').trim();
        if (defaultOfficeId === '266') {
            sandboxIssues.push('Default Office ID (266)');
        } else if (defaultOfficeId === '25000000000392') {
            sandboxIssues.push('Default Office ID (25000000000392)');
        }

        const medId = getEffectiveFieldValueByVariable('{MedicalIntegrationConfigId}').trim().toUpperCase();
        if (medId === '22FA374D-7BA2-439F-A1AD-F4712DD882EF') {
            sandboxIssues.push('Medical Integration Config ID (22FA374D-7BA2-439F-A1AD-F4712DD882EF)');
        } else if (medId === '87AC3583-8BFC-439D-9F0B-C7A450CF5E2A') {
            sandboxIssues.push('Medical Integration Config ID (87AC3583-8BFC-439D-9F0B-C7A450CF5E2A)');
        }
    }

    // 3. Telephony proactive response prompt checks (for both Ordering and Dental)
    if (isAiOrdering || isDental) {
        const inboundDeployments = telephonyDeployments.filter(d => !d.useForOutboundCalling);
        if (inboundDeployments.length > 0) {
            inboundDeployments.forEach(d => {
                const isSpanish = d.locale?.toLowerCase().startsWith('es') ?? false;
                let expectedConstant = '';
                if (isAiOrdering) {
                    expectedConstant = isSpanish
                        ? '{{constant.generative_proactive_response_ordering_spanish}}'
                        : '{{constant.proactive_response_prompt}}';
                } else if (isDental) {
                    expectedConstant = isSpanish
                        ? '{{constant.generative_proactive_response_dental_spanish}}'
                        : '{{constant.generative_proactive_response_dental}}';
                }

                const hasProactivePrompt = d.useGenerativeProactiveResponse && (d.generativeProactiveResponsePrompt ?? '').trim().length > 0;
                const nameText = d.name?.trim() ? `telephony deployment '${d.name.trim()}'` : 'an inbound telephony deployment';

                if (!hasProactivePrompt) {
                    otherIssues.push(`proactive response prompt is missing on ${nameText}`);
                } else if (d.generativeProactiveResponsePrompt?.trim() !== expectedConstant) {
                    otherIssues.push(`proactive response prompt on ${nameText} should use constant '${expectedConstant}'`);
                }
            });
        } else {
            otherIssues.push('no proactive response is configured in telephony settings');
        }
    }

    const totalIssuesCount = sandboxIssues.length + otherIssues.length;
    if (totalIssuesCount === 1) {
        if (sandboxIssues.length === 1) {
            const item = sandboxIssues[0];
            if (item.includes('Admin - Location ID (10138)')) {
                msgs.push('Warning: Admin - Location ID is using the sandbox value (10138). Please update with client POS details.');
            } else if (item.includes('Admin - Location ID (6716858da59fcf87d60ad76b)')) {
                msgs.push('Warning: Admin - Location ID is using the sandbox value (6716858da59fcf87d60ad76b). Please update with client POS details.');
            } else if (item.includes('Admin - Location ID (af8e3bf4-a16b-4cca-b7a7-bb1be0a16021)')) {
                msgs.push('Warning: Admin - Location ID is using the sandbox value (af8e3bf4-a16b-4cca-b7a7-bb1be0a16021). Please update with client POS details.');
            } else if (item.includes('Cancellation Code')) {
                msgs.push('Warning: Admin Cancellation Code ID is using the sandbox value (473). Please update with client PMS details.');
            } else if (item.includes('Referral Type')) {
                msgs.push('Warning: Admin Referral Type Code is using the sandbox value (ROO6). Please update with client PMS details.');
            } else if (item.includes('Default Office ID (266)')) {
                msgs.push('Warning: Default Office ID is using the sandbox value (266). Please update with client PMS details.');
            } else if (item.includes('Default Office ID (25000000000392)')) {
                msgs.push('Warning: Default Office ID is using the sandbox value (25000000000392). Please update with client PMS details.');
            } else if (item.includes('Procedure Types')) {
                msgs.push('Warning: Procedure Types is using sandbox default values. Please update with client PMS details.');
            } else if (item.includes('22FA374D-7BA2-439F-A1AD-F4712DD882EF')) {
                msgs.push('Warning: Medical Integration Config ID is using the sandbox value (22FA374D-7BA2-439F-A1AD-F4712DD882EF). Please update with client PMS details.');
            } else if (item.includes('87AC3583-8BFC-439D-9F0B-C7A450CF5E2A')) {
                msgs.push('Warning: Medical Integration Config ID is using the sandbox value (87AC3583-8BFC-439D-9F0B-C7A450CF5E2A). Please update with client PMS details.');
            }
        } else if (otherIssues.length === 1) {
            const issue = otherIssues[0];
            if (issue.includes('test app')) {
                msgs.push('Warning: This location is marked as a test app, but it is currently live. Please set the Is Test field to False.');
            } else if (issue.includes('proactive response')) {
                msgs.push(`Warning: ${issue.charAt(0).toUpperCase() + issue.slice(1)}.`);
            } else {
                msgs.push('Warning: No proactive response is configured in telephony settings.');
            }
        }
    } else if (totalIssuesCount > 1) {
        let sentence = '';
        if (sandboxIssues.length > 0) {
            const joinedSandbox = sandboxIssues.join(', ').replace(/, ([^,]*)$/, " and $1");
            sentence += `the following fields are using sandbox values: ${joinedSandbox}`;
        }

        if (otherIssues.length > 0) {
            const joinedOther = otherIssues.join(' and ');
            if (sentence) {
                sentence += `, and ${joinedOther}`;
            } else {
                sentence += `${joinedOther}`;
            }
        }

        sentence = `Warning: ${sentence.charAt(0).toUpperCase() + sentence.slice(1)}.`;
        msgs.push(sentence);
    }

    return msgs;
}
